import { createElement, getFocusableElements } from "../utilities";
import { matchers } from "../validate-props";

export const focusTrapNodeTypes = {
    fallback: "fallback",
    start: "start",
    end: "end",
};

/**
 * Handle focus on the the last span control element
 * @param {HTMLElement} element
 * @param {HTMLElement} fallbackElement
 */
const handleStartFocusTrap = (element, fallbackElement) => (e) => {
    const focusableElements = getFocusableElements(element);
    if (!element.contains(e.relatedTarget) && focusableElements.length > 0) {
        focusableElements[0].focus && focusableElements[0].focus();
    } else if (focusableElements.length > 0) {
        focusableElements[focusableElements.length - 1].focus &&
            focusableElements[focusableElements.length - 1].focus();
    } else if (focusableElements.length === 0) {
        fallbackElement && fallbackElement.focus();
    }
};

/**
 * Handle focus on the the last constrol span element
 * @param {HTMLElement} element
 * @param {HTMLElement} fallbackElement
 */
const handleEndFocusTrap = (element, fallbackElement) => (e) => {
    const focusableElements = getFocusableElements(element);
    if (!element.contains(e.relatedTarget) && focusableElements.length > 0) {
        focusableElements[focusableElements.length - 1].focus &&
            focusableElements[focusableElements.length - 1].focus();
    } else if (focusableElements.length > 0) {
        focusableElements[0].focus && focusableElements[0].focus();
    } else if (focusableElements.length === 0) {
        fallbackElement && fallbackElement.focus();
    }
};

/**
 * Dispose listeners from elements
 * @param {HTMLElement} startElement
 * @param {HTMLElement} endElement
 * @param {Function} startEvent
 * @param {Function} endEvent
 */
const disposeFocusTrap = ({ fallbackElement, startElement, endElement, startEvent, endEvent }) => {
    startElement.removeEventListener("focus", startEvent);
    endElement.removeEventListener("focus", endEvent);
    fallbackElement.remove();
    startElement.remove();
    endElement.remove();
};

/**
 * Create focus trap helper nodes
 * @param {HTMLElement} type - one of focusTrapNodeTypes
 * @returns {HTMLElement}
 */
export const createFocusTrapNode = ({ type }) => {
    if (
        matchers.neq(type)(focusTrapNodeTypes.fallback) &&
        matchers.neq(type)(focusTrapNodeTypes.start) &&
        matchers.neq(type)(focusTrapNodeTypes.end)
    ) {
        console.error(
            `createFocusTrapNode type must be one of: 
            ${focusTrapNodeTypes.fallback}, ${focusTrapNodeTypes.start} or ${focusTrapNodeTypes.end}, but received: ${type}`
        );
        return false;
    }

    const node = createElement("span", {
        "aria-hidden": "true",
        "data-focus": type,
        tabindex: type === focusTrapNodeTypes.start || type === focusTrapNodeTypes.end ? "0" : "-1",
    });

    return node;
};

/**
 * Add a focus trap to existing elements
 * Note: focus trap need 3 spans to work properly in all situations
 * Note: useful when using into angular/react applications
 * @param {HTMLElement} element
 * @param {HTMLElement} startElement
 * @param {HTMLElement} endElement
 * @param {HTMLElement} fallbackElement
 * @returns {Object}
 *  - @param {Function} dispose
 */
export const addFocusTrap = ({ element, fallbackElement, startElement, endElement }) => {
    if (!matchers.htmlElement(element)) {
        console.error("addFocusTrap expect element to be a htmlElement");
        return false;
    }

    if (!matchers.htmlElement(fallbackElement)) {
        console.error("addFocusTrap expect fallbackElement to be a htmlElement");
        return false;
    }

    if (!matchers.htmlElement(startElement)) {
        console.error("addFocusTrap expect startElement to be a htmlElement");
        return false;
    }

    if (!matchers.htmlElement(endElement)) {
        console.error("addFocusTrap expect endElement to be a htmlElement");
        return false;
    }

    const startEvent = handleStartFocusTrap(element, fallbackElement);
    const endEvent = handleEndFocusTrap(element, fallbackElement);

    startElement.addEventListener("focus", startEvent);
    endElement.addEventListener("focus", endEvent);

    return {
        dispose: () =>
            disposeFocusTrap({ fallbackElement, startElement, endElement, startEvent, endEvent }),
    };
};

/**
 * Build a focus trap and append to the element, returning dispose method
 * @param {HTMLElement} element
 * @returns {Object}
 *  - @param {Function} dispose
 */
export const buildFocusTrap = (element) => {
    if (!matchers.htmlElement(element)) {
        console.error("buildFocusTrap expect a htmlElement");
        return false;
    }

    const fallbackElement = createFocusTrapNode({ type: focusTrapNodeTypes.fallback });
    const startElement = createFocusTrapNode({ type: focusTrapNodeTypes.start });
    const endElement = createFocusTrapNode({ type: focusTrapNodeTypes.end });

    element.insertAdjacentElement("afterbegin", fallbackElement);
    element.insertAdjacentElement("afterbegin", startElement);
    element.insertAdjacentElement("beforeend", endElement);

    return addFocusTrap({ element, fallbackElement, startElement, endElement });
};
